// Generated by tutorials/record.cjs from tutorials/articles/h26-selective-touch.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h26 --text-only` (wording) or
// `node tutorials/record.cjs h26` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'selective-touch',
  catalogId: 'h26',
  title: 'Taking menu orders with Selective Touch',
  summary: 'Take each resident\'s meal choices on a tablet or touch screen, from a menu that already fits their diet and consistency, and send them to the kitchen.',
  audience: 'Dietary staff who take residents\' menu choices',
  area: 'kitchen',
  learn: [
    'Find residents on the Select Resident list',
    'Pick the meal you are taking orders for',
    'Tap a resident\'s choices onto their tray',
    'What happens when a choice clashes with an allergy',
    'Review and finish the order',
    'Change an order that was already taken',
    'Hold a tray, and how it is released',
    'What a resident who does not order gets',
    'Order for guests, and for several meals ahead',
  ],
  keywords: [
    'selective touch',
    'selective menu',
    'menu choices',
    'order',
    'orders',
    'tablet',
    'touch',
    'kiosk',
    'choices',
    'menu entry',
    'allergy warning',
    'change order',
    'edit order',
    'load prior ticket',
    'start over',
    'clear all',
    'regenerate',
    'reprint',
    'hold tray',
    'held tray',
    'resume',
    'kitchen display',
    'no order',
    'did not order',
    'default menu',
    'guest',
    'guest tray',
    'visitor',
    'meals ahead',
    'next meal',
  ],
  steps: [
    {
      id: 'open',
      part: 'Find the resident',
      heading: 'Open Selective Touch',
      text: 'In the menu, select Kitchen, then Selective Touch. It opens full screen, ready for a tablet. Select Resident lists everyone you can take an order for.',
      media: {
        image: {
          src: '/help/selective-touch/open.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Select Resident screen with the residents listed by name, the date and meal button, and the All, No Order, Order Taken and Printed filters.',
      },
    },
    {
      id: 'meal',
      part: 'Find the resident',
      heading: 'Pick the meal',
      text: 'The blue button at the top shows the date and meal you are taking orders for. Select it to pick another, or select the next meal\'s button at the bottom right.',
      media: {
        image: {
          src: '/help/selective-touch/meal.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/meal.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.5,
        },
        alt: 'The date and meal button now reading Lunch.',
      },
    },
    {
      id: 'resident',
      part: 'Find the resident',
      heading: 'Tap the resident',
      text: 'Tap the resident. Their menu opens: each box is a menu category with its choices, already in the resident\'s consistency.\n\nAt the top left are their room, unit and diet order. FA shows how many food allergies they have.',
      media: {
        image: {
          src: '/help/selective-touch/resident.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/resident.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.8,
        },
        alt: 'Yuki Tanaka\'s lunch menu: Entree, Starch, Vegetable, Dessert and Beverage boxes, with Regular and Thin Liquids on each choice, and Selected Items empty on the right.',
      },
    },
    {
      id: 'choose',
      part: 'Take the order',
      heading: 'Tap the choices',
      text: 'Tap what the resident chooses in each box. Each choice goes onto Selected Items on the right, under its category.\n\nTo take one off, select it in Selected Items, then Delete.',
      media: {
        image: {
          src: '/help/selective-touch/choose.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/choose.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.3,
        },
        alt: 'Selected Items listing Baked Cod, Mashed Potatoes and Green Beans.',
      },
    },
    {
      id: 'allergy',
      part: 'Take the order',
      heading: 'If a choice has an allergen',
      text: 'When an item has something the resident is allergic to, Dietech stops you with an Allergy Warning that names the allergen. Select OK and help the resident choose something else.',
      media: {
        image: {
          src: '/help/selective-touch/allergy.webp',
          width: 672,
          height: 714,
        },
        clip: {
          src: '/help/selective-touch/allergy.mp4',
          width: 672,
          height: 714,
          durationSec: 2,
        },
        alt: 'An Allergy Warning: Tanaka, Yuki is allergic to something in this item. The Peanut Butter Cookie contains PEANUT.',
      },
    },
    {
      id: 'finish-choosing',
      part: 'Take the order',
      heading: 'Finish the choices',
      text: 'Pick the rest of the meal, such as a different dessert and a drink.',
      media: {
        image: {
          src: '/help/selective-touch/finish-choosing.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/finish-choosing.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'Selected Items with five items: Baked Cod, Mashed Potatoes, Green Beans, Vanilla Pudding and Iced Tea.',
      },
    },
    {
      id: 'review',
      part: 'Take the order',
      heading: 'Review',
      text: 'Select Save/Review. Check the items on the right. Under Action, Print Later prints the ticket with the meal\'s tray tickets, Save Only saves it without printing, and Hold Tray keeps it until you release it.',
      media: {
        image: {
          src: '/help/selective-touch/review.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/review.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'The review screen: the Lunch ticket, Print Now, Print Later selected, Save Only and Hold Tray, with the five selected items on the right.',
      },
    },
    {
      id: 'finish',
      part: 'Take the order',
      heading: 'Finish',
      text: 'Select Finish. The order is saved and you are back on Select Resident for the next resident. Pick Order Taken to see who has ordered.',
      media: {
        image: {
          src: '/help/selective-touch/finish.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/finish.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.8,
        },
        alt: 'Select Resident filtered to Order Taken, showing Tanaka, Yuki.',
      },
    },
    {
      id: 'change-open',
      part: 'Change an order already taken',
      heading: 'Open the order again',
      text: 'To change what a resident ordered, tap them again. Use Order Taken to find them quickly.\n\nThe order opens as it was saved: their choices are in Selected Items, with any note and flags.\n\nTo start over, select Clear All. Nothing changes until you finish. Regenerate starts the meal again from the menu, and nothing is deleted until you finish either. For a resident on a static selective menu, Regenerate deletes the saved order as soon as you confirm and brings back their earlier choices.',
      media: {
        image: {
          src: '/help/selective-touch/change-open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/change-open.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.7,
        },
        alt: 'The resident\'s saved lunch order in Selected Items: Baked Cod, Mashed Potatoes, Green Beans, Vanilla Pudding and Iced Tea, with Clear All marked.',
      },
    },
    {
      id: 'change-load',
      part: 'Change an order already taken',
      heading: 'Change the choices',
      text: 'Change what they want: select an item, then Delete, and tap the new choice.\n\nHere the resident wants coffee instead of iced tea.',
      media: {
        image: {
          src: '/help/selective-touch/change-load.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/change-load.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'Selected Items with Baked Cod, Mashed Potatoes, Green Beans, Vanilla Pudding and Coffee.',
      },
    },
    {
      id: 'change-printed',
      part: 'Change an order already taken',
      heading: 'If the ticket already printed',
      text: 'Select Save/Review. When you finish, the new choices replace the old order, so the tray ticket and the kitchen\'s counts use only the new ones.\n\nDietech does not print a new ticket by itself. If the old one already printed, select Print Now to print the new ticket straight away, or Print Later to put it in the queue with the meal\'s other tickets. Then make sure the old ticket comes off the tray.\n\nAn order you open and save again without changing it starts on Save Only, so its ticket is not printed again.',
      media: {
        image: {
          src: '/help/selective-touch/change-printed.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/change-printed.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.8,
        },
        alt: 'The review screen for the changed order, with Print Now, Print Later, Save Only and Hold Tray under Action, marked.',
      },
    },
    {
      id: 'hold',
      part: 'Hold a tray',
      heading: 'Hold the tray',
      text: 'Select Hold Tray when the tray must not go out yet, for example while the resident is at an appointment. The button now reads HOLD: Do Not Deliver.\n\nHolding a tray does not stop its ticket printing: Print Now or Print Later still works as you chose.',
      media: {
        image: {
          src: '/help/selective-touch/hold.webp',
          width: 252,
          height: 884,
        },
        clip: {
          src: '/help/selective-touch/hold.mp4',
          width: 252,
          height: 884,
          durationSec: 2.8,
        },
        alt: 'Under Action, Print Later selected and the hold button reading HOLD: Do Not Deliver, with Add reason (optional) below it.',
      },
    },
    {
      id: 'hold-reason',
      part: 'Hold a tray',
      heading: 'Say why',
      text: 'Select Add reason (optional), then pick a reason or type your own, and select OK. The kitchen sees the reason with the held tray. Then select Finish.',
      media: {
        image: {
          src: '/help/selective-touch/hold-reason.webp',
          width: 584,
          height: 926,
        },
        clip: {
          src: '/help/selective-touch/hold-reason.mp4',
          width: 584,
          height: 926,
          durationSec: 3.5,
        },
        alt: 'The Hold Reason window with Patient in procedure picked, the other reasons below it, and Cancel and OK.',
      },
    },
    {
      id: 'hold-release',
      part: 'Hold a tray',
      heading: 'Release the tray',
      text: 'On the Kitchen Display (Kitchen, then Kitchen Display), the order is marked HELD with its reason, so the kitchen keeps the tray back. When the tray can go, the kitchen selects Resume on its card.\n\nIf your facility does not use the Kitchen Display, tell the kitchen about a held tray yourself.',
      media: {
        image: {
          src: '/help/selective-touch/hold-release.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Kitchen Display with Tanaka, Yuki\'s lunch marked HELD, Hold: Patient in procedure, and a Resume button.',
      },
    },
    {
      id: 'no-order',
      part: 'Residents who do not order',
      heading: 'Who has not ordered',
      text: 'Pick No Order to see who still needs to choose for this meal.\n\nA resident who never orders still gets a tray. Their tray ticket has the default choices your menu cycle plans for their diet order and consistency, the same as for a resident who does not choose from a selective menu. If Static Selective Menu is checked for the resident, their last choices for that menu day are used again.',
      media: {
        image: {
          src: '/help/selective-touch/no-order.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/no-order.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'Select Resident filtered to No Order for lunch, listing the residents who have not chosen yet.',
      },
    },
    {
      id: 'guest-add',
      part: 'Guests and meals ahead',
      heading: 'Order for a guest',
      text: 'When a visitor eats with a resident, select Add Guest at the bottom. Tap the resident the guest is with, pick how many guests, then select OK.',
      media: {
        image: {
          src: '/help/selective-touch/guest-add.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/guest-add.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'Select which resident to add guest(s) for, with Abernathy, Gloria checked and 2 picked among the numbers at the bottom.',
      },
    },
    {
      id: 'guest-list',
      part: 'Guests and meals ahead',
      heading: 'Take the guest\'s order',
      text: 'Each guest is listed under the resident as GUEST 1, GUEST 2 and so on, for this meal only, with your facility\'s house diet. Tap a guest to take their order the same way as a resident\'s.',
      media: {
        image: {
          src: '/help/selective-touch/guest-list.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/selective-touch/guest-list.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.4,
        },
        alt: 'Two new cards, Abernathy, GUEST 1 and Abernathy, GUEST 2, each marked Guest, and a message that 2 guests were added.',
      },
    },
    {
      id: 'ahead',
      part: 'Guests and meals ahead',
      heading: 'Order several meals ahead',
      text: 'You can take a resident\'s choices for several meals at once. On their menu, select the date and meal button and pick the next meal, or Add Other Date for a day further ahead, then tap their choices for that meal.\n\nSave/Review then shows a ticket for each meal you filled in, and Finish saves them all together.',
      media: {
        image: {
          src: '/help/selective-touch/ahead.webp',
          width: 698,
          height: 568,
        },
        clip: {
          src: '/help/selective-touch/ahead.mp4',
          width: 698,
          height: 568,
          durationSec: 2,
        },
        alt: 'The Select Date & Meal window, with a tile for each upcoming meal and Add Other Date at the bottom.',
      },
    },
  ],
  tips: [
    'Sort the list by Room or Tray Line to go door to door, or pick No Order to see who still needs to choose.',
    'Select the arrow button at the bottom right to go straight to the next meal for the same resident.',
    'Tap the i on an item to see what is in it before the resident decides.',
  ],
  notes: [
    'The menu comes from your menu cycle for the resident\'s diet order, so each choice is already in their consistency. Their meal pattern items and adjustments still apply to the tray.',
    'Whether staff can serve an item past an allergy warning depends on their role. Most roles cannot.',
    'Print Now sends the ticket to a printer straight away. Print Later prints it with the rest of the meal\'s tray tickets. Save Only saves the order without printing it or putting it in the queue.',
  ],
  related: ['tray-tickets', 'allergies-likes-dislikes', 'meal-patterns', 'printers-and-printing'],
  recorded: '2026-11-03',
};

export default article;
